import { useTranslation } from 'react-i18next'
import { Waypoints } from 'lucide-react'

import { ObjectPicker } from '@/components/ObjectPicker'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { LineItemsApi, type LineItem } from '@/services/lineItems/lineItemsApi'

/**
 * Line items whose name or references contain the term, led by the one whose
 * id it is. Ids are not text columns the list search can match, so a term
 * that could be an id is also looked up as one.
 */
async function searchLineItems(term: string): Promise<LineItem[]> {
  const text = term.trim()
  const [byId, byName] = await Promise.all([
    text && !/\s/.test(text) ? LineItemsApi.getByIds([text]).catch(() => []) : [],
    LineItemsApi.getAll({ search: text, objectState: 'all' }),
  ])
  return [...byId, ...byName.filter((lineItem) => !byId.some((match) => match.id === lineItem.id))]
}

export interface DiagnosticLineItemPickerProps {
  value?: LineItem
  onChange: (lineItem: LineItem | undefined) => void
}

/** Picks the one line item a diagnostic run reports on, by name or id. */
export function DiagnosticLineItemPicker({ value, onChange }: DiagnosticLineItemPickerProps) {
  const { t } = useTranslation()
  return (
    <Card tone="insight">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CardIcon icon={Waypoints} />
          {t('diagnostics.lineItem')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* One line item at most: picking another replaces it. */}
        <ObjectPicker<LineItem>
          queryKey="diagnostics-line-item-search"
          search={searchLineItems}
          selected={value ? [value] : []}
          onChange={(next) => onChange(next.at(-1))}
          addLabel={t('diagnostics.pickLineItem')}
          searchPlaceholder={t('diagnostics.searchLineItem')}
          emptyText={t('diagnostics.allLineItems')}
          renderMeta={(lineItem) => lineItem.id}
        />
      </CardContent>
    </Card>
  )
}
